import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { NZ_CITIES } from "@/lib/format";
import { ImageUpload, GalleryUpload } from "@/components/ImageUpload";

export const EMPTY_BUSINESS = {
  business_name: "", owner_name: "", email: "", phone: "", category_id: "", description: "", short_description: "",
  years_in_business: 0, suburb: "", city: "", postcode: "", service_area: "", profile_image: "", cover_image: "", gallery: [],
};

export const pickBusiness = (p) => Object.fromEntries(Object.keys(EMPTY_BUSINESS).map((k) => [k, p[k] ?? EMPTY_BUSINESS[k]]));

const Field = ({ label, children, className = "" }) => (
  <div className={className}><Label>{label}</Label><div className="mt-1.5">{children}</div></div>
);

export function DetailsFields({ value, onChange, categories }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Business name" className="sm:col-span-2"><Input data-testid="biz-name" value={value.business_name} onChange={set("business_name")} className="h-12" /></Field>
      <Field label="Owner name"><Input data-testid="biz-owner" value={value.owner_name} onChange={set("owner_name")} className="h-12" /></Field>
      <Field label="Business category">
        <select data-testid="biz-category" value={value.category_id} onChange={set("category_id")} className="h-12 w-full rounded-md border border-input bg-white px-3 text-sm">
          <option value="">Select a category</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Business email"><Input data-testid="biz-email" type="email" value={value.email} onChange={set("email")} className="h-12" /></Field>
      <Field label="Phone"><Input data-testid="biz-phone" value={value.phone} onChange={set("phone")} className="h-12" /></Field>
      <Field label="Years in business"><Input data-testid="biz-years" type="number" min="0" value={value.years_in_business} onChange={set("years_in_business")} className="h-12" /></Field>
      <Field label="Short tagline (shown on cards)"><Input data-testid="biz-short" maxLength={200} value={value.short_description} onChange={set("short_description")} className="h-12" /></Field>
      <Field label="About your business (min 20 characters)" className="sm:col-span-2"><Textarea data-testid="biz-description" rows={5} value={value.description} onChange={set("description")} /></Field>
    </div>
  );
}

export function LocationFields({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Field label="Suburb"><Input data-testid="biz-suburb" value={value.suburb} onChange={set("suburb")} placeholder="e.g. Ponsonby" className="h-12" /></Field>
      <Field label="City">
        <Input data-testid="biz-city" list="nz-cities" value={value.city} onChange={set("city")} placeholder="e.g. Auckland" className="h-12" />
        <datalist id="nz-cities">{NZ_CITIES.map((c) => <option key={c} value={c} />)}</datalist>
      </Field>
      <Field label="Postcode"><Input data-testid="biz-postcode" value={value.postcode} onChange={set("postcode")} className="h-12" /></Field>
      <Field label="Service area" className="sm:col-span-3"><Input data-testid="biz-area" value={value.service_area} onChange={set("service_area")} placeholder="e.g. Central & West Auckland" className="h-12" /></Field>
    </div>
  );
}

export function ImagesFields({ value, onChange }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-[180px_1fr]">
        <ImageUpload label="Profile image" testId="upload-profile" value={value.profile_image} onChange={(u) => onChange({ ...value, profile_image: u })} />
        <ImageUpload label="Cover image" testId="upload-cover" aspect="aspect-[16/7]" value={value.cover_image} onChange={(u) => onChange({ ...value, cover_image: u })} />
      </div>
      <GalleryUpload value={value.gallery} onChange={(g) => onChange({ ...value, gallery: g })} />
    </div>
  );
}

export function validateBusiness(v) {
  if (v.business_name.trim().length < 2) return "Enter your business name";
  if (v.owner_name.trim().length < 2) return "Enter the owner name";
  if (!/\S+@\S+\.\S+/.test(v.email)) return "Enter a valid business email";
  if (v.phone.trim().length < 5) return "Enter a phone number";
  if (!v.category_id) return "Choose a category";
  if (v.description.trim().length < 20) return "Description must be at least 20 characters";
  return null;
}

export function validateLocation(v) {
  if (v.suburb.trim().length < 2 || v.city.trim().length < 2) return "Enter your suburb and city";
  return null;
}
